import React from 'react';
import { motion } from 'framer-motion';

const ProjectsSection = () => {
  const projects = [
    {
      title: 'Rollout',
      tagline: 'Group ride RSVP — no account required',
      description:
        "Built for the Nashville cycling community. Organizers create recurring weekly rides, share the link, and see who's rolling. Guests can RSVP with just a name — no Strava account needed. Features recurring series that auto-generate a fresh RSVP list each week, ride tagging, and an admin dashboard.",
      tech: ['React', 'TypeScript', 'Supabase', 'Vercel'],
      highlights: [
        'Guest RSVP without signup',
        'Auto-generating weekly rides',
        'Ride categorization & filtering',
      ],
      liveUrl: 'https://rollout.himynameiseli.com',
      githubUrl: 'https://github.com/eliwemyss/rollout',
    },
  ];

  const styles = {
    section: {
      paddingTop: '4rem',
      paddingBottom: '4rem',
      backgroundColor: '#111111',
      color: '#ffffff',
    },
    container: {
      maxWidth: '900px',
      margin: '0 auto',
      paddingLeft: '1.5rem',
      paddingRight: '1.5rem',
    },
    heading: {
      fontSize: '2.25rem',
      fontWeight: 'bold',
      textAlign: 'center',
      marginBottom: '0.75rem',
      color: '#ffffff',
    },
    subheading: {
      fontSize: '1.1rem',
      textAlign: 'center',
      marginBottom: '3rem',
      color: '#9ca3af',
    },
    card: {
      backgroundColor: '#1a1a1a',
      borderRadius: '1rem',
      border: '1px solid #2a2a2a',
      padding: '2rem',
      transition: 'border-color 0.3s ease',
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: '1rem',
      marginBottom: '1rem',
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: 'bold',
      marginBottom: '0.25rem',
    },
    tagline: {
      color: '#9ca3af',
      fontSize: '0.95rem',
    },
    links: {
      display: 'flex',
      gap: '0.75rem',
    },
    liveLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.4rem',
      padding: '0.5rem 1rem',
      borderRadius: '0.5rem',
      fontWeight: '600',
      fontSize: '0.875rem',
      backgroundColor: 'rgba(22, 163, 74, 0.15)',
      color: '#16a34a',
      border: '1px solid rgba(22, 163, 74, 0.3)',
      textDecoration: 'none',
      transition: 'background-color 0.2s ease',
    },
    githubLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.4rem',
      padding: '0.5rem 1rem',
      borderRadius: '0.5rem',
      fontWeight: '600',
      fontSize: '0.875rem',
      border: '1px solid #3a3a3a',
      color: '#d1d5db',
      textDecoration: 'none',
      transition: 'border-color 0.2s ease',
    },
    description: {
      color: '#d1d5db',
      lineHeight: '1.7',
      marginBottom: '1.5rem',
    },
    highlightsRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    highlight: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.875rem',
      color: '#d1d5db',
    },
    dot: {
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      backgroundColor: '#16a34a',
      flexShrink: 0,
    },
    techRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
    },
    techPill: {
      padding: '0.3rem 0.75rem',
      fontSize: '0.75rem',
      fontWeight: '600',
      borderRadius: '9999px',
      backgroundColor: '#0f0f0f',
      color: '#9ca3af',
      border: '1px solid #2a2a2a',
    },
  };

  return (
    <section id="projects-section" style={styles.section}>
      <div style={styles.container}>
        <motion.h2
          style={styles.heading}
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          What I'm Building
        </motion.h2>
        <motion.p
          style={styles.subheading}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
        >
          Real problems, real users, shipped code
        </motion.p>

        {projects.map((project, idx) => (
          <motion.div
            key={idx}
            style={styles.card}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div style={styles.cardHeader}>
              <div>
                <h3 style={styles.title}>{project.title}</h3>
                <p style={styles.tagline}>{project.tagline}</p>
              </div>
              <div style={styles.links}>
                <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" style={styles.liveLink}>
                  ↗ Live App
                </a>
                <a href={project.githubUrl} target="_blank" rel="noopener noreferrer" style={styles.githubLink}>
                  Code
                </a>
              </div>
            </div>
            <p style={styles.description}>{project.description}</p>
            <div style={styles.highlightsRow}>
              {project.highlights.map((h, hIdx) => (
                <div key={hIdx} style={styles.highlight}>
                  <span style={styles.dot} />
                  {h}
                </div>
              ))}
            </div>
            <div style={styles.techRow}>
              {project.tech.map((t, tIdx) => (
                <span key={tIdx} style={styles.techPill}>{t}</span>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default ProjectsSection;
